import { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Camera, Loader2, Sparkles, UserPlus, RefreshCw, Archive, Search } from 'lucide-react';
import { intakeAPI, contactsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

/**
 * Review dialog for an extraction result + match candidates.
 * Used by the screenshot upload flow and the email-forward queue.
 *
 * Session 132 — the dialog now leads with an explicit choice:
 *   (•) Add new contact   ( ) Update existing contact
 * "Update" surfaces the auto-detected match candidates AND a search box
 * so the user can pick ANY existing contact to merge the scan into.
 *
 * Optional props:
 *   addToGroupId  – pin the resulting contact into a specific group on create
 *                   AND update. Used by the group-detail "Scan contact" button.
 *   onPrefill     – if supplied, "Add new contact" renders a "Use these
 *                   details" CTA instead of writing to the DB; the parent
 *                   (e.g. NewContactPage) receives the edited `extracted`
 *                   payload and fills its own form.
 */
export function IntakeReviewDialog({ open, onClose, extracted: initial, candidates, queueId, onApplied, addToGroupId, onPrefill }) {
  const navigate = useNavigate();
  const [extracted, setExtracted] = useState(initial || {});
  const [mode, setMode] = useState(candidates?.length ? 'update' : 'new');
  const [target, setTarget] = useState(candidates?.[0]?.id || null);
  const [supersede, setSupersede] = useState(Boolean(initial?.supersede_hint));
  const [saving, setSaving] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const searchTimer = useRef(null);

  const setField = (k, v) => setExtracted((e) => ({ ...e, [k]: v }));
  const setPhone = (i, v) => setExtracted((e) => ({ ...e, phones: e.phones.map((p, idx) => idx === i ? { ...p, number: v } : p) }));
  const setEmail = (i, v) => setExtracted((e) => ({ ...e, emails: e.emails.map((em, idx) => idx === i ? { ...em, email: v } : em) }));

  // Debounced contact search for "Update existing contact" mode.
  useEffect(() => {
    if (mode !== 'update') return undefined;
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults([]); return undefined; }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      try {
        const { data } = await contactsAPI.list({ search: q, limit: 8 });
        const rows = Array.isArray(data) ? data : (data.contacts || data.items || []);
        const candidateIds = new Set((candidates || []).map((c) => c.id));
        setSearchResults(rows.filter((r) => !candidateIds.has(r.id)));
      } catch { /* best-effort */ }
    }, 300);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [searchQ, mode, candidates]);

  const apply = async (action) => {
    setSaving(true);
    try {
      const payload = {
        extracted,
        action,
        target_contact_id: action === 'update' ? target : undefined,
        supersede,
        add_to_group_id: addToGroupId || undefined,
      };
      const res = queueId
        ? (await intakeAPI.applyQueued(queueId, payload)).data
        : (await intakeAPI.confirm(payload)).data;
      const msg = action === 'create'
        ? (addToGroupId ? 'Contact created and added to group' : 'Contact created')
        : (addToGroupId ? 'Contact updated and added to group' : 'Contact updated');
      toast.success(msg);
      onApplied?.(res);
      onClose();
      // When add_to_group_id is set we stay on the group page; otherwise
      // navigate to the new contact like before.
      if (res.contact_id && !addToGroupId) navigate(`/contacts/${res.contact_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    }
    setSaving(false);
  };

  // "Fill form" mode: skip the DB write entirely and hand the (edited) payload
  // back to the parent. Used by NewContactPage so the user reviews & edits in
  // the same form they already have open instead of jumping to a new screen.
  const handlePrefill = () => {
    onPrefill?.(extracted);
    onClose();
  };

  const conf = Math.round((extracted.confidence || 0) * 100);

  const contactRow = (c, testIdPrefix) => (
    <label key={c.id} className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-card" data-testid={`${testIdPrefix}-${c.id}`}>
      <input type="radio" name="target" checked={target === c.id} onChange={() => setTarget(c.id)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{c.first_name} {c.last_name}</p>
        <p className="text-[11px] text-muted-foreground truncate">
          {(c.emails?.[0]?.email) || (c.phones?.[0]?.number) || ''}
        </p>
      </div>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="intake-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-nav-blue" /> Review captured contact
          </DialogTitle>
          <DialogDescription className="text-xs">
            Confidence: <span className={`font-semibold ${conf >= 70 ? 'text-emerald-600' : 'text-amber-600'}`}>{conf}%</span>
            {extracted.supersede_hint && <span className="ml-2 text-amber-600">· {extracted.supersede_hint}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {/* Editable extracted fields */}
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">First name</Label><Input value={extracted.first_name || ''} onChange={(e) => setField('first_name', e.target.value)} data-testid="intake-first" /></div>
            <div><Label className="text-xs">Last name</Label><Input value={extracted.last_name || ''} onChange={(e) => setField('last_name', e.target.value)} data-testid="intake-last" /></div>
          </div>
          {(extracted.phones || []).map((p, i) => (
            <div key={`p${i}`}>
              <Label className="text-xs">Phone ({p.label || 'mobile'})</Label>
              <Input value={p.number || ''} onChange={(e) => setPhone(i, e.target.value)} data-testid={`intake-phone-${i}`} />
            </div>
          ))}
          {(extracted.emails || []).map((em, i) => (
            <div key={`e${i}`}>
              <Label className="text-xs">Email ({em.label || 'personal'})</Label>
              <Input value={em.email || ''} onChange={(e) => setEmail(i, e.target.value)} data-testid={`intake-email-${i}`} />
            </div>
          ))}
          {(extracted.company || extracted.title) && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Company</Label><Input value={extracted.company || ''} onChange={(e) => setField('company', e.target.value)} /></div>
              <div><Label className="text-xs">Title</Label><Input value={extracted.title || ''} onChange={(e) => setField('title', e.target.value)} /></div>
            </div>
          )}
          {extracted.address && (
            <div><Label className="text-xs">Address</Label><Input value={extracted.address} onChange={(e) => setField('address', e.target.value)} /></div>
          )}

          {/* Session 132 — explicit new-vs-update choice */}
          <div className="border border-border rounded-xl p-3 bg-secondary/30 space-y-1.5" data-testid="intake-mode-card">
            <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-card">
              <input type="radio" name="intake-mode" checked={mode === 'new'} onChange={() => setMode('new')} data-testid="intake-mode-new" />
              <span className="text-sm font-medium">Add new contact</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-card">
              <input type="radio" name="intake-mode" checked={mode === 'update'} onChange={() => setMode('update')} data-testid="intake-mode-update" />
              <span className="text-sm font-medium">
                Update existing contact
                {candidates?.length > 0 && (
                  <span className="ml-1.5 text-[11px] font-normal text-amber-600">
                    {candidates.length} possible match{candidates.length === 1 ? '' : 'es'} found
                  </span>
                )}
              </span>
            </label>
          </div>

          {/* Update mode — candidates + search-any-contact picker */}
          {mode === 'update' && (
            <div className="border border-border rounded-xl p-3 bg-secondary/30 space-y-2" data-testid="intake-update-panel">
              {candidates?.length > 0 && (
                <>
                  <p className="text-xs font-heading font-semibold">Possibly the same person?</p>
                  {candidates.map((c) => contactRow(c, 'intake-candidate'))}
                </>
              )}
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Search your contacts…"
                  className="pl-8 h-9"
                  data-testid="intake-target-search"
                />
              </div>
              {searchResults.map((c) => contactRow(c, 'intake-search-result'))}
              {searchQ.trim().length >= 2 && searchResults.length === 0 && (!candidates || candidates.every((c) => !`${c.first_name} ${c.last_name}`.toLowerCase().includes(searchQ.trim().toLowerCase()))) && (
                <p className="text-[11px] text-muted-foreground px-1" data-testid="intake-search-empty">No contacts match "{searchQ.trim()}"</p>
              )}
              <label className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                <span className="text-xs flex items-center gap-1">
                  <Archive size={12} /> Replace existing phone/email of the same label
                </span>
                <Switch checked={supersede} onCheckedChange={setSupersede} data-testid="intake-supersede-toggle" />
              </label>
            </div>
          )}

          <div className="flex gap-2 pt-1">
            {mode === 'update' ? (
              <Button
                onClick={() => apply('update')}
                disabled={saving || !target}
                className="flex-1 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full"
                data-testid="intake-update-btn"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <><RefreshCw size={14} className="mr-1" />Update selected contact</>}
              </Button>
            ) : onPrefill ? (
              <Button
                onClick={handlePrefill}
                className="flex-1 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full"
                data-testid="intake-prefill-btn"
              >
                <UserPlus size={14} className="mr-1" /> Use these details
              </Button>
            ) : (
              <Button onClick={() => apply('create')} disabled={saving} className="flex-1 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full" data-testid="intake-create-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <><UserPlus size={14} className="mr-1" />{addToGroupId ? 'Create & add to group' : 'Create new contact'}</>}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


/** Scan trigger button — session 132: one tap opens the NATIVE file
 *  picker directly (no intermediate menu, no instructions modal). On
 *  iOS/Android the OS chooser already offers Take Photo / Photo
 *  Library / Choose File, so the in-app menu was redundant.
 *
 *  Props:
 *    addToGroupId  – pinned through to the confirm payload.
 *    onPrefill     – switches the review dialog into "fill the parent's form"
 *                    mode (skip DB write; return the edited extraction).
 *    onCreated     – fires after a successful DB create/update so the caller
 *                    can refresh its own state (e.g. group members list).
 *    label         – optional override for the button text. Falls back to
 *                    "Scan screenshot".
 *    icon          – optional override for the icon element. Defaults to Camera.
 */
export function ScreenshotIntakeButton({
  children,
  className = '',
  testId = 'intake-screenshot-btn',
  addToGroupId,
  onPrefill,
  onCreated,
  label,
  icon,
}) {
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);
  const hasLabelOverride = label !== undefined && label !== null;
  const labelText = hasLabelOverride ? label : (children || 'Scan screenshot');

  const processFile = async (file) => {
    if (!file) return;
    if (!/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error('Please pick a PNG, JPEG, or WEBP image');
      return;
    }
    setPending(true);
    try {
      const { data } = await intakeAPI.screenshot(file);
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OCR failed');
    }
    setPending(false);
  };

  const onFileChosen = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    processFile(file);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onFileChosen}
        data-testid={`${testId}-library-input`}
      />
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className={`${typeof children === 'function' || (children && typeof children !== 'string') ? 'block w-full cursor-pointer text-left' : 'inline-flex items-center justify-center gap-2 cursor-pointer'} ${className}`}
        data-testid={testId}
        aria-label={typeof labelText === 'string' && labelText ? labelText : 'Scan'}
        disabled={pending}
      >
        {typeof children === 'function' ? children({ pending }) : (children && typeof children !== 'string') ? children : (
          <>
            {pending ? <Loader2 size={16} className="animate-spin" /> : (icon || <Camera size={16} />)}
            {typeof labelText === 'string' && labelText ? <span>{pending ? 'Reading…' : labelText}</span> : null}
          </>
        )}
      </button>

      {result && (
        <IntakeReviewDialog
          open
          onClose={() => setResult(null)}
          extracted={result.extracted}
          candidates={result.candidates}
          addToGroupId={addToGroupId}
          onPrefill={onPrefill}
          onApplied={onCreated}
        />
      )}
    </>
  );
}
